import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import LogoutButton from './LogoutButton';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { performLogout, updateCredits, updateUser } from '../slices/authSlice';
import authFetch from '../utils/authFetch';
import {
    Bars3Icon,
    XMarkIcon,
    DocumentTextIcon,
    Cog6ToothIcon,
    CreditCardIcon,
    ClockIcon,
    UserCircleIcon,
    ChevronDownIcon,
    ArrowRightOnRectangleIcon,
    GiftIcon
} from '@heroicons/react/24/outline';

const CreditsDisplay = () => {
    const dispatch = useDispatch();
    const userId = useSelector((state) => state.auth.user?.id);
    const credits = useSelector((state) => state.auth.user?.credits);
    const hasPaid = useSelector((state) => state.auth.user?.hasPaid);
    const numCredits = parseFloat(credits) || 0;

    useEffect(() => {
        if (!userId) return;
        let cancelled = false;
        (async () => {
            try {
                const response = await authFetch(`https://cvanalyzerback.osc-fr1.scalingo.io/api/users/${userId}/credits`);
                if (!response.ok || cancelled) return;
                const data = await response.json();
                if (cancelled) return;
                if (data.credits !== undefined) dispatch(updateCredits(data.credits));
                if (typeof data.hasPaid === 'boolean') dispatch(updateUser({ hasPaid: data.hasPaid }));
            } catch {
                // le header affiche le solde déjà en mémoire
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [userId, dispatch]);
    const needsUnlock = !hasPaid;
    const isEmpty = hasPaid && numCredits <= 0;
    const isLow = hasPaid && numCredits > 0 && numCredits < 1;

    const colorClass = needsUnlock
        ? 'bg-blue-700 border-blue-500 hover:bg-blue-600'
        : isEmpty
        ? 'bg-amber-900/40 border-amber-700 hover:bg-amber-900/60'
        : isLow
        ? 'bg-amber-900/40 border-amber-700 hover:bg-amber-900/60'
        : 'bg-slate-800 border-slate-700 hover:bg-slate-700';

    const iconColor = needsUnlock ? 'text-white' : isEmpty || isLow ? 'text-amber-300' : 'text-slate-400';
    const textColor = needsUnlock ? 'text-white' : isEmpty || isLow ? 'text-amber-200' : 'text-slate-100';

    return (
        <div className="relative group">
            <Link
                to="/subscription"
                className={`flex items-center space-x-1 px-2 py-1.5 rounded-lg border cursor-pointer ${colorClass}`}
            >
                <CreditCardIcon className={`w-4 h-4 ${iconColor}`} />
                <span className={`text-sm font-medium ${textColor}`}>
                    {needsUnlock ? 'Rapport 5 €' : numCredits}
                </span>
            </Link>
            <div className="absolute right-0 top-full mt-2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 invisible group-hover:opacity-100 group-hover:visible whitespace-nowrap z-50">
                {needsUnlock
                    ? 'Débloquer le rapport complet et le CV'
                    : isEmpty
                    ? 'Recharger pour relancer une analyse'
                    : `${numCredits} crédit${numCredits > 1 ? 's' : ''} — packs et relances`}
            </div>
        </div>
    );
};

const UserMenu = ({ user }) => {
    const [isOpen, setIsOpen] = useState(false);
    const menuRef = useRef(null);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const menuPaths = ['/dashboard', '/admin', '/referral', '/subscription'];
    const isMenuPage = menuPaths.includes(location.pathname);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (menuRef.current && !menuRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]);

    const handleLogout = async () => {
        if (!window.confirm('Êtes-vous sûr de vouloir vous déconnecter ?')) {
            return;
        }

        dispatch(performLogout());
        sessionStorage.clear();
        navigate('/', { replace: true });
        window.location.reload();
    };

    const menuItems = [
        { icon: UserCircleIcon, label: 'Mon compte', path: '/dashboard' },
        { icon: CreditCardIcon, label: 'Débloquer le rapport', path: '/subscription' },
        { icon: GiftIcon, label: 'Parrainer', path: '/referral' },
    ];

    if (user?.username === 'Guillaume') {
        menuItems.push({
            icon: Cog6ToothIcon,
            label: 'Admin',
            path: '/admin',
            className: 'border-t border-gray-200 mt-1 pt-1',
        });
    }

    return (
        <div className="relative" ref={menuRef}>
            <button
                type="button"
                onClick={() => setIsOpen(!isOpen)}
                className={`flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-medium ${
                    isMenuPage
                        ? 'bg-white/10 text-white'
                        : 'text-slate-200 hover:bg-white/10'
                }`}
            >
                <UserCircleIcon className="w-5 h-5" />
                <span className="hidden sm:inline">{user?.username || 'Mon compte'}</span>
                <ChevronDownIcon className={`w-4 h-4 ${isOpen ? 'rotate-180' : ''}`} />
            </button>

            {isOpen && (
                <div className="absolute right-0 top-full mt-2 w-56 bg-white rounded-lg border border-gray-200 z-50 overflow-hidden">
                    <div className="py-1">
                        {menuItems.map((item) => {
                            const Icon = item.icon;
                            return (
                                <Link
                                    key={item.path}
                                    to={item.path}
                                    onClick={() => setIsOpen(false)}
                                    className={`flex items-center space-x-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 ${
                                        location.pathname === item.path ? 'bg-blue-50 text-blue-700' : ''
                                    } ${item.className || ''}`}
                                >
                                    <Icon className="w-5 h-5" />
                                    <span>{item.label}</span>
                                </Link>
                            );
                        })}
                        <div className="border-t border-gray-200 mt-1 pt-1">
                            <button
                                type="button"
                                onClick={handleLogout}
                                className="flex items-center space-x-3 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 w-full text-left"
                            >
                                <ArrowRightOnRectangleIcon className="w-5 h-5" />
                                <span>Déconnexion</span>
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

const Header = () => {
    const { token, user } = useSelector((state) => state.auth);
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const location = useLocation();

    const isActive = (path) => location.pathname === path;
    const navClasses = (path) => {
        const base = 'px-3 py-2 rounded-lg text-sm font-medium';
        if (isActive(path)) {
            return `${base} bg-white/10 text-white`;
        }
        return `${base} text-slate-300 hover:bg-white/10 hover:text-white`;
    };
    const mobileNav = (path) =>
        `flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-medium ${
            isActive(path) ? 'bg-white/10 text-white' : 'text-slate-300 hover:bg-white/10 hover:text-white'
        }`;

    return (
        <header className="fixed top-0 left-0 right-0 z-50 bg-slate-900 border-b border-slate-800">
            <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex items-center justify-between h-14">
                    <Link to={token ? '/upload' : '/'} className="flex items-center space-x-2 min-w-0">
                        <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center shrink-0">
                            <DocumentTextIcon className="w-5 h-5 text-white" />
                        </div>
                        <span className="text-lg font-semibold text-white truncate">CV Analyse</span>
                    </Link>

                    <div className="hidden lg:flex items-center space-x-1">
                        {!token && (
                            <>
                                <Link to="/" className={navClasses('/')}>Accueil</Link>
                                <Link to="/methode" className={navClasses('/methode')}>Méthode</Link>
                                <Link to="/guides" className={navClasses('/guides')}>Guides</Link>
                                <Link to="/pricing" className={navClasses('/pricing')}>Tarifs</Link>
                                <Link to="/register" className={navClasses('/register')}>S&apos;inscrire</Link>
                                <Link to="/login" className="btn-nav-primary ml-1">Se connecter</Link>
                            </>
                        )}

                        {token && (
                            <>
                                <Link to="/upload" className="btn-nav-primary whitespace-nowrap">
                                    Analyser
                                </Link>
                                <Link to="/history" className={navClasses('/history')}>
                                    Historique
                                </Link>
                                <CreditsDisplay />
                                <UserMenu user={user} />
                            </>
                        )}
                    </div>

                    <div className="lg:hidden shrink-0">
                        <button
                            type="button"
                            onClick={() => setIsMenuOpen(!isMenuOpen)}
                            className="p-2 rounded-lg text-slate-200 hover:bg-white/10"
                            aria-label={isMenuOpen ? 'Fermer le menu' : 'Ouvrir le menu'}
                        >
                            {isMenuOpen ? <XMarkIcon className="w-6 h-6" /> : <Bars3Icon className="w-6 h-6" />}
                        </button>
                    </div>
                </div>

                {isMenuOpen && (
                    <div className="lg:hidden pb-3">
                        <div className="px-2 pt-2 pb-3 space-y-1 bg-slate-800 border border-slate-700 rounded-lg mt-2">
                            {!token && (
                                <>
                                    <Link to="/" className={mobileNav('/')} onClick={() => setIsMenuOpen(false)}>Accueil</Link>
                                    <Link to="/methode" className={mobileNav('/methode')} onClick={() => setIsMenuOpen(false)}>Méthode</Link>
                                    <Link to="/guides" className={mobileNav('/guides')} onClick={() => setIsMenuOpen(false)}>Guides</Link>
                                    <Link to="/pricing" className={mobileNav('/pricing')} onClick={() => setIsMenuOpen(false)}>Tarifs</Link>
                                    <Link to="/register" className={mobileNav('/register')} onClick={() => setIsMenuOpen(false)}>S&apos;inscrire</Link>
                                    <Link to="/login" className={mobileNav('/login')} onClick={() => setIsMenuOpen(false)}>Se connecter</Link>
                                </>
                            )}

                            {token && (
                                <>
                                    <Link to="/upload" className={mobileNav('/upload')} onClick={() => setIsMenuOpen(false)}>
                                        <DocumentTextIcon className="w-4 h-4" />
                                        <span>Analyser</span>
                                    </Link>
                                    <Link to="/history" className={mobileNav('/history')} onClick={() => setIsMenuOpen(false)}>
                                        <ClockIcon className="w-4 h-4" />
                                        <span>Historique</span>
                                    </Link>
                                    <div className="px-3 py-2">
                                        <CreditsDisplay />
                                    </div>
                                    <div className="border-t border-slate-700 my-1" />
                                    <Link to="/dashboard" className={mobileNav('/dashboard')} onClick={() => setIsMenuOpen(false)}>
                                        <UserCircleIcon className="w-4 h-4" />
                                        <span>Mon compte</span>
                                    </Link>
                                    <Link to="/subscription" className={mobileNav('/subscription')} onClick={() => setIsMenuOpen(false)}>
                                        <CreditCardIcon className="w-4 h-4" />
                                        <span>Débloquer le rapport</span>
                                    </Link>
                                    <Link to="/referral" className={mobileNav('/referral')} onClick={() => setIsMenuOpen(false)}>
                                        <GiftIcon className="w-4 h-4" />
                                        <span>Parrainer</span>
                                    </Link>
                                    {user?.username === 'Guillaume' && (
                                        <Link to="/admin" className={mobileNav('/admin')} onClick={() => setIsMenuOpen(false)}>
                                            <Cog6ToothIcon className="w-4 h-4" />
                                            <span>Admin</span>
                                        </Link>
                                    )}
                                    <div className="pt-2 border-t border-slate-700 mt-1">
                                        <LogoutButton />
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                )}
            </nav>
        </header>
    );
};

export default Header;
